import { getCatalogFilterPageKind } from '@/features/seo-listing/consts';
import { getLockedFacetsFromFilters } from '@/features/seo-listing/utils/build-catalog-filter-fetch-plan.util';
import type { CatalogFilterPageKind } from '@/features/seo-listing/types';
import { doesFilterStateMatchPermalinkIdentity } from '../does-filter-state-match-permalink-identity';

interface FilterReloadInput {
  selectedFandoms: string[];
  selectedCharacters: string[];
  selectedTags: string[];
  selectedUniverses: string[];
  selectedPairingCharacters: string[];
}

export interface FilterReloadContext {
  pageKind: CatalogFilterPageKind;
  lockedFacets: ReturnType<typeof getLockedFacetsFromFilters>;
  selectedCharacterIds: string[];
  selectedTagIds: string[];
}

/**
 * When the address bar is still the legacy catalog (`/fanfics?fandoms=…`)
 * because SEO redirect is missing/404, filter dictionaries must follow the
 * same locked identity as the matching permalink page. Otherwise characters
 * stay on the global catalog list (e.g. Harry Potter while Half-Life is checked).
 *
 * Important: do NOT elevate to `character` on catalog. Character clientReload
 * skips the characters facet (assumes SSR already loaded fandom characters).
 * On legacy GET that skip leaves/restores the global catalog dictionary after
 * a character checkbox is toggled — selected char on top, HP/Ninjago below.
 * Keep `fandom` so characters stay `fandomEntity` while one fandom is selected.
 *
 * Stale permalink path (SEO pending after filters were cleared) must not keep
 * `fandom`/`character` pageKind — fandomEntity without fandomId returns an
 * empty characters dictionary and leaves the facet blank on /fanfics.
 */
export const resolveFilterReloadPageKind = (
  pathname: string,
  filters: FilterReloadInput,
): CatalogFilterPageKind => {
  const pathKind = getCatalogFilterPageKind(pathname);
  if (pathKind !== 'catalog') {
    const pathIdentityMatches = doesFilterStateMatchPermalinkIdentity(pathname, {
      selectedFandoms: filters.selectedFandoms,
      selectedCharacters: filters.selectedCharacters,
      selectedTags: filters.selectedTags,
      selectedPairingCharacters: filters.selectedPairingCharacters,
    });
    if (pathIdentityMatches) return pathKind;
  }

  if (filters.selectedFandoms.length === 1) {
    return 'fandom';
  }
  if (filters.selectedTags.length === 1) {
    return 'tag';
  }
  return 'catalog';
};

/**
 * Builds context for filter options reload based on pathname and current filters
 */
export const buildFilterReloadContext = (
  pathname: string,
  filters: FilterReloadInput,
): FilterReloadContext => {
  const pageKind = resolveFilterReloadPageKind(pathname, filters);
  return {
    pageKind,
    lockedFacets: getLockedFacetsFromFilters(pageKind, {
      fandoms: filters.selectedFandoms,
      characters: filters.selectedCharacters,
      tags: filters.selectedTags,
      universes: filters.selectedUniverses,
      pairingCharacters: filters.selectedPairingCharacters,
    }),
    selectedCharacterIds: filters.selectedCharacters,
    selectedTagIds: filters.selectedTags,
  };
};
